import React from 'react'
import PropTypes from 'prop-types'
import AutosizeInput from 'react-input-autosize'

import { formatMoney } from 'client/griffin'
import { normalizeAmountInput } from 'client/lib/amount'

const MAX_SIZE = 8

function dupeZeroes(numZeroes) {
  return new Array(Math.max(0, numZeroes + 1)).join('0')
}

class CurrencyTextBoxRegular extends React.Component {
  static propTypes = {
    currencyCode: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    decimalDigits: PropTypes.number,
    decimalDot: PropTypes.string,
    value: PropTypes.number,
    autoResize: PropTypes.bool,
    showCurrency: PropTypes.bool, // TODO:  rename this to showCurrencySymbol.
    removeTrailingZeros: PropTypes.bool,
    focusOnMount: PropTypes.number,
    decimalDotInRegex: PropTypes.string,
    showIntlCurrencyCode: PropTypes.bool,
  }

  static defaultProps = {
    value: 0,
    autoResize: false,
    removeTrailingZeros: false,
    showCurrency: true,
    showIntlCurrencyCode: true,
  }

  constructor(props) {
    super(props)
    const majorDigits = MAX_SIZE - props.decimalDigits // no. of digits to left of dot (integer part)
    const minorDigits = props.decimalDigits // no. of digits to right of dot (fractional part)
    let minorPart = ''
    const decimalDotInRegex = '[\\.,]'
    if (minorDigits > 0) {
      minorPart = `(${decimalDotInRegex}\\d{0,${minorDigits}})?`
    }

    this.state = {
      decimalDot: props.decimalDot,
      allowedInputRegex: new RegExp(
        `^(([1-9]\\d{0,${majorDigits - 1}})|0)?${minorPart}$`
      ),
      zeroFractionalString: props.decimalDot + dupeZeroes(minorDigits),
      value: props.value || '', // Note: this deliberately includes the case where value === ''.
      unformattedValue: '',
    }
  }

  UNSAFE_componentWillReceiveProps = ({ value }) => {
    const inputElement = this.getInputElement()
    if (value !== this.props.value && document.activeElement !== inputElement) {
      this.setState({ value }, () => {
        this.formatValueWithGriffin()
      })
    }
  }

  getInputElement = () => {
    let inputElement = this.inputElement
    if (this.props.autoResize && inputElement.getInput) {
      inputElement = inputElement.getInput()
    }
    return inputElement
  }

  getAmountVal = () => {
    let value = normalizeAmountInput(this.getInputElement().value)
    if (value === this.state.decimalDot) {
      value = `0${this.state.decimalDot}`
    }
    return value
  }

  isInputEmpty = () => {
    const amountVal = this.getAmountVal()
    return amountVal == null || amountVal === '' || amountVal === '0'
  }

  isInputValid = () => {
    return (
      this.isInputEmpty() ||
      this.state.allowedInputRegex.test(this.getAmountVal())
    )
  }

  handleChange = event => {
    const value = normalizeAmountInput(event.target.value)
    if (!this.isInputValid()) {
      // this.showInvalid() - shake it, baby
      return
    }
    this.setState({ value })
    if (value === '') {
      this.props.onChange(undefined)
    } else {
      this.props.onChange(parseFloat(value.replace(this.state.decimalDot, '.')))
    }
  }

  handleFocus = () => {
    if (!this.getInputElement()) {
      return
    }
    this.restoreUnformattedValue()
  }

  formatValueWithGriffin = () => {
    this.setState(prevState => {
      let newValue = ''
      if (prevState.value) {
        const prevValue = prevState.value.toString()
        newValue = formatMoney(
          this.props.currencyCode,
          prevValue.replace(this.props.decimalDot, '.'),
          {
            showCurrencySymbol: this.props.showCurrency,
            hideCurrencyCode: !this.props.showIntlCurrencyCode,
          }
        )
      }
      return {
        value: newValue,
        unformattedValue: prevState.value,
      }
    })
  }

  restoreUnformattedValue = () => {
    this.setState(prevState => ({
      value: prevState.unformattedValue,
    }))
  }

  handleBlur = () => {
    this.formatValueWithGriffin(this.props.onBlur())
  }

  componentDidMount() {
    if (this.props.focusOnMount != null) {
      let inputElement = this.getInputElement()
      setTimeout(() => {
        this.formatValueWithGriffin()
        inputElement.focus()
      }, this.props.focusOnMount)
    }
  }

  render() {
    const { ...inputProps } = this.props
    delete inputProps.currencyCode
    delete inputProps.decimalDigits
    delete inputProps.decimalDot
    delete inputProps.autoResize
    delete inputProps.showCurrency
    delete inputProps.removeTrailingZeros
    delete inputProps.focusOnMount
    delete inputProps.showIntlCurrencyCode

    inputProps.onChange = this.handleChange
    inputProps.onFocus = this.handleFocus
    inputProps.onBlur = this.handleBlur
    inputProps.ref = inputElement => {
      this.inputElement = inputElement
    }
    inputProps.value = this.state.value

    if (this.props.autoResize) {
      inputProps.inputClassName = inputProps.className
      delete inputProps.className
      return <AutosizeInput {...inputProps} />
    }
    inputProps.type = 'text'
    return <input {...inputProps} />
  }
}

export default CurrencyTextBoxRegular
